import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Share2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ShareChatDialog, type ShareLinkData } from "./share-chat-dialog";
import { cn } from "@/utils/cn";

export interface ShareChatHandlers {
  fetchShare: (sessionId: string) => Promise<ShareLinkData | null>;
  createShare: (
    sessionId: string,
    expiresInDays: number | null,
  ) => Promise<ShareLinkData | null>;
  revokeShare: (sessionId: string) => Promise<void>;
  isFetching?: boolean;
  isCreating?: boolean;
  isRevoking?: boolean;
}

interface Props {
  sessionId: string;
  conversationTitle?: string;
  handlers: ShareChatHandlers;
  variant?: "icon" | "button";
  /** Restyle the trigger (e.g. to match a toolbar's ghost icons). */
  triggerClassName?: string;
}

export const ShareChatTrigger = ({
  sessionId,
  conversationTitle,
  handlers,
  variant = "button",
  triggerClassName,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState<ShareLinkData | null>(null);

  const openDialog = async () => {
    setExisting(null);
    setOpen(true);
    try {
      const data = await handlers.fetchShare(sessionId);
      setExisting(data);
    } catch {
      setExisting(null);
    }
  };

  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="outline"
          size="icon"
          className={cn("rounded-full size-8", triggerClassName)}
          onClick={openDialog}
          aria-label={t("common:a11y.share_chat")}
          data-tooltip-id="hrm-tooltip"
          data-tooltip-content={t("common:a11y.share_chat")}
        >
          <Share2 size={16} />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={openDialog}
          className="gap-2 rounded-full"
        >
          <Share2 size={14} /> {t("common:ai_assistant.share")}
        </Button>
      )}
      <ShareChatDialog
        open={open}
        onOpenChange={setOpen}
        conversationTitle={conversationTitle}
        existing={existing}
        isLoading={handlers.isFetching}
        isCreating={handlers.isCreating}
        isRevoking={handlers.isRevoking}
        onCreate={(d) => handlers.createShare(sessionId, d)}
        onRevoke={() => handlers.revokeShare(sessionId)}
      />
    </>
  );
};
